import React, { useState, useEffect, useContext } from 'react';
import {
  Tabs,
  Alert,
  Mask,
} from 'admin-base-component-library';
import {
  withStyles,
  CircularProgress,
} from '@material-ui/core';
import flow from 'lodash.flow';
import { withTranslation } from 'react-i18next';

import UserContext from '../../context/UserContext';
import useValidation from '../../hooks/useValidation';

import {
  BUDGET_STATUS,
  createBudget,
  getBudgetById,
  getBudgetByIdWithResponse,
  getChildBudget,
  updateBudget,
  updateBudgetResponse,
  updateChildBudget,
} from '../../services/budget';
import { getRestaurantByID } from '../../services/restaurant';
import { getProductsByCode } from '../../services/product';
import { numberToCurrency, numberToFloat, TimestampToDateWithFormatUS } from '../../services/Service';

import General from './Tabs/General.js';
import Products from './Tabs/Products.js';
import FranchiseesRestaurant from './Tabs/FranchiseesRestaurant.js';

import styles from './styles';

import validation, { saveValidation, saveQuantityValidation, sendBudget } from './validation';
import { USER_ROLES } from '../../services/user';

const ProductEdit = ({
  t,
  match: { params },
  classes,
  history,
}) => {
  const { user } = useContext(UserContext);
  const [restaurant, setRestaurant] = useState();
  const { validate } = useValidation();
  const [products, setProducts] = useState([]);
  const [general, setGeneral] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [changeRoute, setChangeRoute] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState();
  const [isParent, setIsParent] = useState(false);
  const [parentRestaurant, setParentRestaurant] = useState();
  const [statusBudget, setStatusBudget] = useState(null);
  const [requestBudgetId, setRequestBudgetId] = useState('');
  const [isChildBudget, setIsChildBudget] = useState(!!history.location?.state?.isChildBudget);
  const [childBudgetId, setChildBudgetId] = useState('');
  const [isEditQuantity, setIsEditQuantity] = useState(false);
  const [isBudgetDuplicate, setIsBudgetDuplicate] = useState(false);
  const [franchiseeRestaurantId, setFranchiseeRestaurantId] = useState('');
  const [productIdsArr, setProductIdsArr] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [mainBudgetId, setMainBudgetId] = useState(null);
  const [mainBudgetRestaurantId, setMainBudgetRestaurantId] = useState(null);
  const [pageType, setPageType] = useState(null);

  const isFranchise = user?.type === USER_ROLES.franchise;

  useEffect(() => {
    if (isBudgetDuplicate && !loading) {
      Alert({
        title: t('Duplicar orçamento'),
        text: t('Você está duplicando este orçamento. Edite as informações desejadas e, ao salvar ou enviar, será gerada uma nova cópia. As alterações não afetam o orçamento original.'),
        type: 'info',
      });
    }
  }, [isBudgetDuplicate, loading]);

  useEffect(() => {
    if (changeRoute === true) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setChangeRoute(false);
      }, 300);
    }
  }, [changeRoute]);

  useEffect(() => {
    const init = async () => {
      try {
        if (params.idDuplicate === 'produtos') {
          return;
        }
        await localStorage.removeItem('budgetId');
        const budgetStatus = history?.location?.state?.status;
        setRequestBudgetId(history?.location?.state?.requestBudgetId);
        setFranchiseeRestaurantId(history?.location?.state?.restaurantId);
        setRestaurant(await getRestaurantByID(user.restaurant.id));
        setChildBudgetId(history?.location?.state?.idChildBudget);
        if (user.restaurant.parent) {
          setParentRestaurant(await getRestaurantByID(user.restaurant.parent));
        }

        if (history?.location?.state?.isEditQuantity) {
          setIsEditQuantity(true);
        }

        if (!!history?.location?.state?.isDuplicate) {
          setIsBudgetDuplicate(true);
        }

        if (budgetStatus) {
          setStatusBudget(budgetStatus);
        }

        if (history.location?.state?.type) {
          setPageType(history.location?.state?.type);
        }

        if (history.location?.state?.idMainBudget
          && history.location?.state?.mainBudgetRestaurantId) {
          const fetchBudgetById = await getBudgetByIdWithResponse(
            history.location?.state?.idMainBudget,
            history.location?.state?.mainBudgetRestaurantId,
            isFranchise,
          );

          const productIds = [];
          fetchBudgetById.responses.forEach(i => i.products.forEach(p => productIds
            .push(
              {
                productId: p.id,
                unitPrice: p.unitPrice,
                observation: p.observation,
                idSupplier: i.idSupplier,
              },
            )));

          if (!isFranchise) {
            setStatusBudget(fetchBudgetById.budget.status);
          }

          setMainBudgetId(history.location?.state?.idMainBudget);
          setMainBudgetRestaurantId(history.location?.state?.mainBudgetRestaurantId);
          setProductIdsArr(productIds);
          setBudgetData(fetchBudgetById);
        }

        setIsParent(user?.type === USER_ROLES.master);

        if (params.idDuplicate) {
          setLoading(true);
          const {
            name,
            deadline,
            endsAt,
            deliveryTime,
            observation,
            products: currentProducts,
          } = await getBudgetById({
            budgetId: params.idDuplicate,
            isFranchise: user?.type === USER_ROLES.franchise,
            budgetStatus,
            isParent: user?.type === USER_ROLES.master,
            isChildBudget: isChildBudget || history?.location?.state?.isChildBudget,
          });

          if (isFranchise && !budgetStatus) {
            const {
              name: parentBudgetName,
              deadline: parentBudgetDeadline,
              endsAt: parentBudgetEndsAt,
              deliveryTime: parentBudgetDeliveryTime,
              observation: parentBudgetObservation,
            } = await getBudgetById({
              budgetId: history?.location?.state?.requestBudgetId,
              budgetStatus,
            });
            setGeneral({
              name: parentBudgetName,
              deadline: parentBudgetDeadline,
              endsAt: parentBudgetEndsAt ? TimestampToDateWithFormatUS(parentBudgetEndsAt) : null,
              deliveryTime: parentBudgetDeliveryTime,
              observation: parentBudgetObservation,
            });
          } else {
            setGeneral({
              name,
              deadline,
              endsAt: endsAt ? TimestampToDateWithFormatUS(endsAt) : null,
              deliveryTime,
              observation,
            });
          }

          const fetchProducts = currentProducts ? await getProductsByCode(
            currentProducts?.map(({ productId }) => productId),
          ) : [];

          const formatProducts = fetchProducts.reduce((allProducts, product) => ({
            ...allProducts,
            [product.id]: product,
          }), {});

          if (currentProducts) {
            setProducts(currentProducts.map(current => ({
              ...current,
              name: formatProducts[current.productId]?.name || current.name,
              segments: formatProducts[current.productId]?.segments || current.segments,
              unit: formatProducts[current.productId]?.unit || current.unit,
              selected: false,
            })));
          }
        }
        setLoading(false);
      } catch {
        setLoading(false);
      }
    };

    init();
  }, [params.idDuplicate]);

  const baseUrl = params.idDuplicate && params.idDuplicate !== t('produtos')
    ? `/${t('orcamentos')}/${t(params.type)}/${params.idDuplicate}`
    : `/${t('orcamentos')}/${t(params.type)}`;


  const formatProductsByQuantity = items => items.reduce((acc, item) => {
    const index = acc.findIndex(p => p.productId === item.productId);
    if (index > -1) {
      acc[index].quantity = numberToCurrency(
        numberToFloat(acc[index].quantity) + numberToFloat(item.quantity),
      );
    } else {
      acc.push({ ...item });
    }
    return acc;
  }, []);


  const addProduct = (currentProduct) => {
    const checkProducts = products.filter(product => (
      product.productId === currentProduct.product.value
    ));
    if (checkProducts.length > 0) {
      Alert({
        title: t('Produto Duplicado'),
        text: t('O produto que você está tentando adicionar já está na lista.'),
        type: 'error',
      });
      return false;
    }
    setSelectedProduct();
    setProducts([
      ...products,
      {
        productId: currentProduct.product.value,
        productName: currentProduct.product.label,
        quantity: currentProduct.quantity,
        unit: currentProduct.unit || currentProduct.product.unit,
        observation: currentProduct.observation,
        segments: currentProduct.product.segments || [],
      },
    ]);
    return true;
  };

  const updateProduct = (value, row, field) => {
    const newProducts = [...products];
    const newRow = { ...row };
    newRow[field] = field !== 'quantity' ? value : Mask.maskCurrencyField(value);
    const index = newProducts.indexOf(row);
    newProducts[index] = newRow;
    setProducts(newProducts);
  };

  const handleCancel = async () => {
    if (history.action === 'POP' || !isChildBudget) {
      const statusGoBackToBudget = statusBudget === BUDGET_STATUS.OPEN
        || statusBudget === BUDGET_STATUS.FINALIZED;

      return history.push(
        budgetData.budget?.id && params.type === t('editar') && statusGoBackToBudget && user.type !== USER_ROLES.franchise
          ? `/${t('orcamento-resposta')}/${budgetData.budget.id}`
          : `/${t('orcamentos')}`,
      );
    }

    setIsChildBudget(false);

    if (pageType === t('editar') && (!isChildBudget || budgetData.budget.status === BUDGET_STATUS.REQUESTED)) {
      return history.push(`/${t('orcamentos')}/${t('editar')}/${budgetData.budget.id}/${t('restaurantes-associados')}`);
    }

    if (!!childBudgetId && !isParent) {
      return history.push({
        pathname: `/${t('orcamento-resposta')}/${childBudgetId}`,
        state: {
          isParent,
          restaurantId: user.restaurant.id,
          status: budgetData.budget.status,
          idMainBudget: mainBudgetId || history.location?.state?.idMainBudget,
          franchiseesRestaurantView: true,
          type: params?.type,
        },
      });
    }

    return history.push(`/${t('orcamento-resposta')}/${budgetData.budget.id}/${t('restaurantes-associados')}`);
  };

  const removeProduct = item => Alert({
    title: t('Cuidado!'),
    text: `
      ${t('Esta ação não pode ser desfeita.')}
      ${t('Tem certeza que deseja excluir')}
      ${t('este produto')}?
      `,
    type: 'warning',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: t('Excluir'),
    cancelButtonText: t('Cancelar'),
  }).then((result) => {
    if (result.value) {
      setProducts(products.filter(({ productId }) => item.productId !== productId));
    }
  });

  const handleSend = async ({ childProducts }) => {
    if (sending) return;

    setSending(true);

    const baseValues = {
      restaurantId: user?.restaurant?.id,
      restaurantSuppliers: isFranchise ? parentRestaurant?.suppliers : user?.restaurant?.suppliers,
      idMainBudget: isFranchise ? (requestBudgetId || params?.idDuplicate) : undefined,
      ...general,
      products: childProducts || products,
    };

    const values = { ...baseValues };

    const validationRules = childProducts ? sendBudget() : validation();

    const handleNoSuppliers = (noSuppliers) => {
      if (noSuppliers.length === 0) return null;

      const joinedProducts = noSuppliers.join(', ');
      const singular = noSuppliers.length === 1;

      return {
        title: t('Atenção'),
        text: singular
          ? t('O seu pedido de orçamento foi enviado com sucesso! Obs - O produto {{productName}} não tem fornecedores vinculados.', { productName: noSuppliers[0] })
          : t('O seu pedido de orçamento foi enviado com sucesso! Obs - Os produtos {{productName}} não tem fornecedores vinculados.', { productName: joinedProducts }),
        type: 'warning',
      };
    };

    validate(validationRules, values, () => {
      Alert({
        title: t('Cuidado!'),
        text: isFranchise
          ? t('Você tem certeza que finalizou o orçamento e gostaria de enviá-lo?')
          : t('Você tem certeza que finalizou o orçamento e gostaria de enviar aos fornecedores?'),
        type: 'warning',
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: t('Prosseguir'),
        cancelButtonText: t('Cancelar'),
      }).then(async (result) => {
        if (!result.value) {
          setSending(false);
          return;
        }

        try {
          let alertData = {
            title: t('Orçamento Enviado'),
            text: t('Orçamento enviado com sucesso.'),
            type: 'success',
          };

          if (params.type === t('editar')) {
            if (isFranchise && statusBudget !== BUDGET_STATUS.REQUESTED) {
              await updateChildBudget({
                ...values,
                status: BUDGET_STATUS.SENT,
                budgetId: params.idDuplicate,
                sentAt: new Date(),
              });
            } else if (!isFranchise) {
              const { noSuppliers } = await updateBudget({
                ...values,
                status: BUDGET_STATUS.OPEN,
                budgetId: params.idDuplicate,
              }, statusBudget === BUDGET_STATUS.SAVED);

              const warning = handleNoSuppliers(noSuppliers);
              if (warning) alertData = warning;
            }
          } else {
            const { noSuppliers } = await createBudget({
              ...values,
              status: isFranchise ? BUDGET_STATUS.SENT : BUDGET_STATUS.OPEN,
            });

            alertData = handleNoSuppliers(noSuppliers) || alertData;
          }

          Alert({
            title: alertData.title,
            text: alertData.text,
            type: alertData.type,
          });
          history.push(`/${t('orcamentos')}`);
        } catch (error) {
          Alert(t('Erro!'), t('Falha ao enviar o orçamento.'), 'error');
        } finally {
          setSending(false);
        }
      });
    });
  };

  const handleSave = async () => {
    let values = {
      restaurantId: franchiseeRestaurantId || user.restaurant.id,
      restaurantSuppliers: user.restaurant.suppliers,
      ...general,
      products,
    };

    if (isFranchise) {
      values = {
        ...values,
        restaurantId: user.restaurant.id,
        restaurantSuppliers: parentRestaurant.suppliers,
        idMainBudget: requestBudgetId || params.idDuplicate,
      };
    }

    const requiresQuantityCheck = (
      (params.type === t('editar') && isFranchise && statusBudget !== BUDGET_STATUS.REQUESTED)
      || (isParent && isChildBudget)
      || (params.type === t('editar') && isEditQuantity)
    );

    if (requiresQuantityCheck) {
      if (products.some(p => p.quantity === '')) {
        Alert({
          title: t('Erro!'),
          text: 'É necessário adicionar a quantidade para todos os produtos',
          type: 'error',
          showCloseButton: true,
        });
        return;
      }

      values.endsAt = new Date();
      values.restaurantSuppliers = isFranchise ? parentRestaurant.suppliers
        : user.restaurant.suppliers;
    }

    const validationRules = isEditQuantity ? saveQuantityValidation() : saveValidation();

    validate(validationRules, values, () => {
      Alert({
        title: t('Cuidado!'),
        text: isParent && !isChildBudget
          ? t('Você tem certeza que finalizou a solicitação de produtos e gostaria de enviar aos restaurantes franqueados?')
          : t('Você tem certeza que finalizou o orçamento e gostaria de salvá-lo?'),
        type: 'warning',
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: t('Prosseguir'),
        cancelButtonText: t('Cancelar'),
      }).then(async (result) => {
        if (!result.value) return;

        const saveBudgetAndRedirect = async (status, budgetId = null) => {
          const saveFunction = budgetId ? updateBudget : createBudget;
          await saveFunction({
            ...values,
            status,
            budgetId,
          });

          let title = '';
          let text = '';

          title = t('Orçamento Adicionado');
          text = t('Orçamento salvo com sucesso.');
          if (isParent) {
            title = t('Solicitação Adicionada');
            text = t('Solicitação de produtos salvo com sucesso.');
            if (budgetId) {
              title = t('Solicitação Editada');
              text = t('Solicitação editada com sucesso.');
            }
          } else if (budgetId) {
            title = t('Orçamento Editado');
            text = t('Orçamento editado com sucesso.');
          }

          Alert({ title, text, type: 'success' });
          history.push(`/${t('orcamentos')}`);
        };

        const updateResponses = async (formattedProducts) => {
          const priceMap = productIdsArr.reduce((map, p) => {
            const matchingProduct = formattedProducts.find(fp => fp.productId === p.productId);
            if (matchingProduct) {
              const priceTotal = numberToCurrency(numberToFloat(matchingProduct.quantity)
                * numberToFloat(p.unitPrice));
              map.set(`${p.productId}-${p.idSupplier}`, {
                priceTotal,
                observation: p.observation,
                unitPrice: p.unitPrice,
              });
            }
            return map;
          }, new Map());

          const updatedResponses = budgetData.responses.map(response => ({
            ...response,
            products: response.products.map((product) => {
              const priceInfo = priceMap.get(`${product.id}-${response.idSupplier}`);
              return priceInfo
                ? { ...product, priceTotal: priceInfo.priceTotal }
                : product;
            }),
          }));

          await Promise.all(updatedResponses.map(updateBudgetResponse));
        };

        const goToBudgetView = () => {
          if (history.action === 'POP') {
            history.push(`/${t('orcamentos')}`);
            return;
          }

          if (pageType === t('editar') && (!isChildBudget || budgetData.budget.status === BUDGET_STATUS.REQUESTED)) {
            history.push(`/${t('orcamentos')}/${t('editar')}/${budgetData.budget.id}/${t('restaurantes-associados')}`);
            return;
          }

          if (isChildBudget && childBudgetId) {
            history.push({
              pathname: `/${t('orcamento-resposta')}/${childBudgetId}`,
              state: {
                isParent,
                restaurantId: user.restaurant.id,
                status: budgetData.status,
                idMainBudget: history.location?.state?.idMainBudget,
                franchiseesRestaurantView: true,
                type: params?.type,
              },
            });
            return;
          }

          history.push(`/${t('orcamento-resposta')}/${budgetData.budget.id}/${t('restaurantes-associados')}`);
        };
        if ((params.type === t('editar') && isFranchise && statusBudget !== BUDGET_STATUS.REQUESTED) || (isParent && isChildBudget)) {
          await updateChildBudget({
            ...values,
            status: isChildBudget ? BUDGET_STATUS.SENT : BUDGET_STATUS.SAVED,
            budgetId: params.idDuplicate,
          });

          const childBudget = await getChildBudget({
            filters: [{ field: 'idMainBudget', type: '==', value: mainBudgetId }],
          });

          const allChildProducts = childBudget.flatMap(cb => cb.products);
          const formattedProducts = formatProductsByQuantity(allChildProducts);

          await updateResponses(formattedProducts);

          Alert({
            title: isParent ? t('Solicitação Editada') : t('Orçamento Editado'),
            text: isParent ? t('Solicitação editada com sucesso.') : t('Orçamento editado com sucesso.'),
            type: 'success',
          });

          setIsChildBudget(false);
          goToBudgetView();
          return;
        }

        if (params.type === t('editar') && isEditQuantity && user.type === USER_ROLES.restaurant) {
          await updateBudget({
            ...values,
            status: statusBudget,
            budgetId: params.idDuplicate,
          });

          const formattedProducts = formatProductsByQuantity(values.products);

          await updateResponses(formattedProducts);

          Alert({
            title: isParent ? t('Solicitação Editada') : t('Orçamento Editado'),
            text: isParent ? t('Solicitação editada com sucesso.') : t('Orçamento editado com sucesso.'),
            type: 'success',
          });

          setIsChildBudget(false);
          history.push({
            pathname: `/${t('orcamento-resposta')}/${budgetData.budget.id}`,
            state: { ...history.location.state },
          });
          return;
        }

        if (params.type === t('editar') && !isFranchise) {
          await saveBudgetAndRedirect(isParent ? BUDGET_STATUS.REQUESTED
            : BUDGET_STATUS.SAVED, params.idDuplicate);
          return;
        }

        await saveBudgetAndRedirect(isParent ? BUDGET_STATUS.REQUESTED : BUDGET_STATUS.SAVED);
      });
    });
  };

  const handleProducts = async () => {
    const values = {
      restaurantId: user.restaurant.id,
      restaurantSuppliers: user.restaurant.suppliers,
      ...general,
      products,
    };

    validate(saveValidation(), values, async () => {
      if (params.type === t('editar')) {
        const { budgetId } = await updateBudget({
          ...values,
          status: BUDGET_STATUS.SAVED,
          budgetId: params.idDuplicate,
        });

        await localStorage.setItem('budgetId', JSON.stringify(budgetId));

        if (selectedProduct) {
          history.push(`/${t('produtos')}/${t('editar')}/${selectedProduct?.value}`);
          return;
        }

        history.push(`/${t('produtos')}/${t('novo')}`);
        return;
      }

      const { budgetId } = await createBudget({ ...values, status: BUDGET_STATUS.SAVED });

      await localStorage.setItem('budgetId', JSON.stringify(budgetId));

      if (selectedProduct) {
        history.push(`/${t('produtos')}/${t('editar')}/${selectedProduct?.value}`);
        return;
      }

      history.push(`/${t('produtos')}/${t('novo')}`);
    });
  };

  const gotoProductsTab = async () => {
    if (isParent && !isChildBudget) {
      await handleSave();
      return;
    }
    setChangeRoute(true);
    history.push(`${baseUrl}/${t('produtos')}`);
  };

  if (loading) {
    return (
      <div className={classes.progressWrapper}>
        <CircularProgress />
      </div>
    );
  }

  return !user ? null : (
    <Tabs
      components={[
        {
          name: 'Geral',
          route: baseUrl,
          screen: (
            <General
              t={t}
              params={params}
              classes={classes}
              history={history}
              handleCancel={handleCancel}
              general={general}
              setGeneral={setGeneral}
              user={user}
              restaurant={restaurant}
              handleSave={gotoProductsTab}
              isParent={isParent}
              isFranchise={user.type === USER_ROLES.franchise}
              isChildBudget={isChildBudget}
              handleSend={handleSend}
              isEditQuantity={isEditQuantity}
              statusBudget={statusBudget}
            />
          ),
        },
        (params.type === 'novo' || !isParent) || (isParent && isChildBudget) ? {
          name: 'Produtos',
          route: `${baseUrl}/${t('produtos')}`,
          hidden: isParent && !isChildBudget,
          screen: (
            <Products
              user={user}
              t={t}
              params={params}
              classes={classes}
              history={history}
              products={products}
              addProduct={addProduct}
              removeProduct={removeProduct}
              updateProduct={updateProduct}
              handleCancel={handleCancel}
              handleSave={handleSave}
              handleSend={handleSend}
              restaurant={restaurant}
              handleProducts={handleProducts}
              setSelectedProduct={setSelectedProduct}
              isFranchise={user.type === USER_ROLES.franchise}
              isParent={isParent}
              isChildBudget={isChildBudget}
              parentRestaurant={parentRestaurant}
              isEditQuantity={isEditQuantity}
              statusBudget={statusBudget}
            />
          ),
        } : {
          name: 'Restaurantes Associados',
          route: `${baseUrl}/${t('restaurantes-associados')}`,
          hidden: !(!!isParent),
          screen: (
            <FranchiseesRestaurant
              t={t}
              params={params}
              classes={classes}
              history={history}
              isParent={isParent}
              setIsChildBudget={setIsChildBudget}
              setLoading={setLoading}
              user={user}
              mainBudgetRestaurantId={mainBudgetRestaurantId}
            />
          ),
        },
      ]}
    />
  );
};

export default flow(
  withStyles(styles, { withTheme: true }),
  withTranslation(),
)(ProductEdit);
